import { LocationVarKey } from "../Generated/LocationVarKey.Data";

/**
 * Canonical registry of basic-data fields for a facility — address, phones,
 * hours, contact emails, district-manager info, etc. Each field has its own
 * Cortex var (one source of truth, full inheritance support).
 *
 * The new Facility Info PM page reads/writes ONLY the Cortex vars; legacy
 * basic-data LocationVarKey entries (Address, Phone, City, …) stay
 * untouched per the no-delete rule. The migration script (batch ε) seeds
 * the Cortex vars from legacy values via `legacyKey`.
 *
 * Distinct from the policy / feature / discount / business-rule / link /
 * integration registries — those carry behavior config; this carries
 * facility identity / contact data.
 *
 * See plan: docs/snowy/plans/2026-05-01-cortex-structured-vars.md
 */

export type FacilityInfoFieldType = "string" | "phone" | "email" | "url" | "select" | "image";

export interface FacilityInfoSelectOption {
  value: string;
  label: string;
}

export interface FacilityInfoDef {
  key: FacilityInfoKey;
  label: string;
  description: string;
  /** The Cortex var holding this field's value. PM InputRow binds here. */
  cortexVarKey: LocationVarKey;
  /**
   * Map back to the legacy var. Used by the migration tool for one-time
   * backfill. Omit when the field is v3-only with no legacy counterpart —
   * the migration spec walk skips entries that don't set this.
   */
  legacyKey?: LocationVarKey;
  /** Optional legacy on/off flag gating the migration — off means the value isn't pulled. */
  legacyHasKey?: LocationVarKey;
  /** True if the migrated value is always location-level and never collapsed to org/group (ENG-3388 D-5). */
  perLocation?: boolean;
  /** Hint for input validation / formatting in PM. */
  fieldType: FacilityInfoFieldType;
  /**
   * Options for `fieldType: "select"` entries. The PM renders a dropdown
   * with these choices. Ignored for non-select field types.
   */
  selectOptions?: FacilityInfoSelectOption[];
  /** Optional URL rendered as a link icon next to the field label in PM. */
  helpUrl?: string;
  /** True if a facility's own public website could plausibly state this — drives what the scraper targets. */
  scrapable?: boolean;
  /**
   * True if this field could plausibly appear in an operator-provided document or spreadsheet
   * (e.g. an internal roster export), even when it would never appear on a public website —
   * drives what Document Ingest's column-mapping picker offers. A field already `scrapable` is
   * implicitly document-sourceable too (public info is a subset of document-statable info); this
   * only needs setting on fields that are document-only, like internal contact info.
   */
  documentSourceable?: boolean;
}

export enum FacilityInfoKey {
  Address = "address",
  City = "city",
  State = "state",
  PostalCode = "postal_code",
  Phone = "phone",
  Email = "email",
  WebsiteURL = "website_url",
  ImageURL = "image_url",
  CountryCode = "country_code",
  PayByPhoneNumber = "pay_by_phone_number",
  AfterHoursPhoneNumber = "after_hours_phone_number",
  BotName = "bot_name",
  DMName = "dm_name",
  DMPhone = "dm_phone",
  DMEmail = "dm_email",
  // Operator / integration identifiers — surfaced as editable fields on
  // the v3 Facility Info page so the bot reads them from Cortex* vars
  // instead of the legacy Name / LocationNumber / FMSId / FMSIntegrationId
  // / BrandId keys.
  Name = "name",
  LocationNumber = "location_number",
  FMSId = "fms_id",
  FMSIntegrationId = "fms_integration_id",
  BrandId = "brand_id",
  // Operator-visible behavior settings — discrete typed vars that
  // weren't a good fit under a prose-policy section. The Greeting
  // topic was removed 2026-05-19 and Persona moved here so operators
  // still have a Details-page surface to set it from.
  Persona = "persona"
}

export const FACILITY_INFO: readonly FacilityInfoDef[] = [
  {
    key: FacilityInfoKey.Name,
    label: "Facility Name",
    description: "The facility's display name - used by the bot in responses and customer-facing communications.",
    cortexVarKey: LocationVarKey.CortexFacilityName,
    legacyKey: LocationVarKey.Name,
    fieldType: "string",
    scrapable: true
  },
  {
    key: FacilityInfoKey.Address,
    label: "Address",
    description: "Street address - shown to users when they ask where you're located.",
    cortexVarKey: LocationVarKey.CortexFacilityAddress,
    legacyKey: LocationVarKey.Address,
    fieldType: "string",
    scrapable: true
  },
  {
    key: FacilityInfoKey.City,
    label: "City",
    description: "City where the facility is located.",
    cortexVarKey: LocationVarKey.CortexFacilityCity,
    legacyKey: LocationVarKey.City,
    fieldType: "string",
    scrapable: true
  },
  {
    key: FacilityInfoKey.State,
    label: "State",
    description: "State or province where the facility is located.",
    cortexVarKey: LocationVarKey.CortexFacilityState,
    legacyKey: LocationVarKey.State,
    fieldType: "string",
    scrapable: true
  },
  {
    key: FacilityInfoKey.PostalCode,
    label: "Postal Code",
    description: "Postal or ZIP code.",
    cortexVarKey: LocationVarKey.CortexFacilityPostalCode,
    legacyKey: LocationVarKey.PostalCode,
    fieldType: "string",
    scrapable: true
  },
  {
    key: FacilityInfoKey.CountryCode,
    label: "Country Code",
    description: "Two-letter country code (e.g. US, CA) - drives postal-code matching in location search.",
    cortexVarKey: LocationVarKey.CortexFacilityCountryCode,
    legacyKey: LocationVarKey.CountryCode,
    fieldType: "string"
  },
  {
    key: FacilityInfoKey.Phone,
    label: "Phone",
    description: "Main phone number - shared by the bot when users ask how to call you.",
    cortexVarKey: LocationVarKey.CortexFacilityPhone,
    legacyKey: LocationVarKey.Phone,
    fieldType: "phone",
    scrapable: true
  },
  {
    key: FacilityInfoKey.Email,
    label: "Email",
    description: "Main email address - shared by the bot when users ask how to email you.",
    cortexVarKey: LocationVarKey.CortexFacilityEmail,
    legacyKey: LocationVarKey.Email,
    fieldType: "email",
    scrapable: true
  },
  {
    key: FacilityInfoKey.WebsiteURL,
    label: "Website URL",
    description: "The facility's unit page - shared by the bot when leads ask about renting or available units.",
    cortexVarKey: LocationVarKey.CortexFacilityWebsiteURL,
    legacyKey: LocationVarKey.WebsiteURL,
    fieldType: "url",
    scrapable: true
  },
  {
    key: FacilityInfoKey.ImageURL,
    label: "Image",
    description: "Photo of this facility - shown on location cards in the chat widget.",
    cortexVarKey: LocationVarKey.CortexFacilityImageURL,
    legacyKey: LocationVarKey.AvatarURL,
    // Uploaded, not typed: PM renders an uploader and stores the resulting URL.
    fieldType: "image"
  },
  // Hours fields (OfficeHours / AccessHours / CallCenterHours / SpecialHours
  // / Timezone) intentionally omitted — they live on the structured Hours
  // page in Details v3, NOT here. Removed 2026-05-02 per Chuck's "no hours
  // prose vars in Facility Info" directive. The underlying LocationVarKey
  // entries are kept for backward-compat data reads but no longer surfaced
  // to operators for editing.
  {
    key: FacilityInfoKey.PayByPhoneNumber,
    label: "Pay-By-Phone Number",
    description: "Phone number shared by the bot when tenants ask how to pay by phone.",
    cortexVarKey: LocationVarKey.CortexFacilityPayByPhoneNumber,
    legacyKey: LocationVarKey.PayByPhoneNumber,
    legacyHasKey: LocationVarKey.HasPayByPhone,
    fieldType: "phone",
    scrapable: true
  },
  {
    key: FacilityInfoKey.AfterHoursPhoneNumber,
    label: "After-Hours Phone Number",
    description: "Phone number shared by the bot when the office is closed and users still need to speak with someone.",
    cortexVarKey: LocationVarKey.CortexFacilityAfterHoursPhoneNumber,
    legacyKey: LocationVarKey.AfterHoursPhoneNumber,
    fieldType: "phone",
    scrapable: true
  },
  {
    key: FacilityInfoKey.BotName,
    label: "Bot Name",
    description: 'Display name the bot uses when introducing itself - e.g. "Hi, I\'m Alex!".',
    cortexVarKey: LocationVarKey.CortexFacilityBotName,
    legacyKey: LocationVarKey.BotName,
    fieldType: "string"
  },
  {
    key: FacilityInfoKey.DMName,
    label: "District Manager: Name",
    description: "District manager's name.",
    cortexVarKey: LocationVarKey.CortexFacilityDMName,
    legacyKey: LocationVarKey.DMName,
    fieldType: "string",
    documentSourceable: true
  },
  {
    key: FacilityInfoKey.DMPhone,
    label: "District Manager: Phone",
    description: "District manager's phone number.",
    cortexVarKey: LocationVarKey.CortexFacilityDMPhone,
    legacyKey: LocationVarKey.DMPhone,
    fieldType: "phone",
    documentSourceable: true
  },
  {
    key: FacilityInfoKey.DMEmail,
    label: "District Manager: Email",
    description: "District manager's email address - used for sending email receipts.",
    cortexVarKey: LocationVarKey.CortexFacilityDMEmail,
    legacyKey: LocationVarKey.DMEmail,
    fieldType: "email",
    documentSourceable: true
  },
  {
    key: FacilityInfoKey.LocationNumber,
    label: "Location Number",
    description: "The internal facility or location number.",
    cortexVarKey: LocationVarKey.CortexFacilityLocationNumber,
    legacyKey: LocationVarKey.LocationNumber,
    fieldType: "string"
  },
  {
    key: FacilityInfoKey.FMSId,
    label: "FMS ID",
    description: "This location's ID in the FMS (Sitelink, Cubby, etc.) - used to sync units, tenants, leases, and leads.",
    cortexVarKey: LocationVarKey.CortexFacilityFMSId,
    legacyKey: LocationVarKey.FMSId,
    perLocation: true,
    fieldType: "string",
    helpUrl: "https://learn.tryswivl.com/fms-integrations#connecting-your-fms"
  },
  {
    key: FacilityInfoKey.FMSIntegrationId,
    label: "FMS Integration ID",
    description: "Which FMS integration drives the sync for this location - configure integrations in Settings.",
    cortexVarKey: LocationVarKey.CortexFacilityFMSIntegrationId,
    legacyKey: LocationVarKey.FMSIntegrationId,
    // Options are the org's configured FMS integrations, populated dynamically at render time (FacilityInfoTopic) — not a static list.
    fieldType: "select",
    helpUrl: "/settings/fms-integrations"
  },
  {
    key: FacilityInfoKey.BrandId,
    label: "Brand ID",
    description: "Brand identifier.",
    cortexVarKey: LocationVarKey.CortexFacilityBrandId,
    legacyKey: LocationVarKey.BrandId,
    fieldType: "string"
  },
  {
    key: FacilityInfoKey.Persona,
    label: "Persona",
    description: "Determines the bot's opening flow - Self Storage uses traditional menus, RV / Boat leads with vehicle storage options.",
    cortexVarKey: LocationVarKey.CortexPersona,
    // No legacy counterpart — Persona is v3-only. The spec walk skips
    // entries without `legacyKey`.
    fieldType: "select",
    selectOptions: [
      { value: "self-storage", label: "Self Storage" },
      { value: "rv-boat", label: "RV / Boat" }
    ]
  }
];

export function findFacilityInfoDef(key: FacilityInfoKey): FacilityInfoDef | undefined {
  return FACILITY_INFO.find((f) => f.key === key);
}
